import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, LockKey, ArrowLeft } from "@phosphor-icons/react";
import { portalApi, PORTAL_TOKEN_KEY } from "@/lib/portalApi";
import { API, formatApiErrorDetail } from "@/lib/api";
import { siteImg } from "@/lib/siteContent";

const MARBLE =
  "https://images.unsplash.com/photo-1558346648-9757f2fa4474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGNhbGFjYXR0YSUyMG1hcmJsZSUyMHNsYWIlMjBsdXh1cnl8ZW58MHx8fHdoaXRlfDE3ODg5MDMzOTh8MA&ixlib=rb-4.1.0&q=85";

// Where to land after signing in: ?next= (portal paths only) or the page that sent us here
export const portalNext = (params) => {
  const raw =
    params?.get("next") || sessionStorage.getItem("bg_portal_next") || "";
  sessionStorage.removeItem("bg_portal_next");
  return raw.startsWith("/portal/") ? raw : "/portal/home";
};

export default function PortalLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [site, setSite] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/site`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setSite)
      .catch(() => {});
  }, []);

  const requestLink = async () => {
    setBusy(true);
    try {
      await portalApi.post("/request-link", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that link",
      );
    }
    setBusy(false);
  };

  const signIn = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/login", {
        email: email.trim(),
        password,
      });
      localStorage.setItem(PORTAL_TOKEN_KEY, data.access_token);
      navigate(portalNext(params), { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not sign in",
      );
    }
    setBusy(false);
  };

  const shop = site?.shop || {};
  const shopName = shop.name || "Brioni Granite";
  const logos = site?.config?.branding || {};
  const lightLogo = logos.logo_regular_light || logos.logo_regular_dark;
  const darkLogo = logos.logo_sticky_dark || logos.logo_sticky_light;

  return (
    <div
      className="site grid min-h-screen bg-[color:var(--s-bg)] lg:grid-cols-[1.05fr_1fr]"
      data-testid="portal-login"
    >
      {/* Left — editorial marble panel */}
      <div className="relative hidden overflow-hidden lg:block">
        <img
          src={MARBLE}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(17,17,20,0.82)] via-[rgba(17,17,20,0.18)] to-[rgba(17,17,20,0.04)]" />
        <div className="relative flex h-full flex-col justify-between p-12 xl:p-16">
          {lightLogo ? (
            <img
              src={siteImg(lightLogo)}
              alt={shopName}
              className="h-10 w-auto max-w-[220px] object-contain"
            />
          ) : shop.has_logo ? (
            <img
              src={`${API}/public/logo`}
              alt={shopName}
              className="h-10 w-auto max-w-[220px] object-contain brightness-0 invert"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className="s-display text-xl uppercase tracking-[0.26em] text-white">
              {shopName}
            </span>
          )}

          <div className="max-w-md">
            <h1 className="s-display text-4xl leading-tight text-white xl:text-5xl">
              Your countertop project, start to finish.
            </h1>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
              See your quote with the shop drawing and the exact slab layout,
              follow your template, fabrication and install dates, browse what
              is in stock and send us a note any time.
            </p>
          </div>

          <span className="s-micro text-white/60">Customer portal</span>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between">
            {darkLogo ? (
              <img
                src={siteImg(darkLogo)}
                alt={shopName}
                className="h-9 w-auto max-w-[190px] object-contain lg:hidden"
              />
            ) : (
              <span className="s-display text-lg uppercase tracking-[0.24em] text-[color:var(--s-ink)] lg:hidden">
                {shopName}
              </span>
            )}
            <Link
              to="/"
              data-testid="portal-back-site"
              className="s-micro ml-auto inline-flex items-center gap-2 text-[color:var(--s-mute)] transition-colors duration-200 hover:text-[color:var(--s-ink)]"
            >
              <ArrowLeft size={13} weight="bold" /> Website
            </Link>
          </div>

          <h2 className="s-display mt-10 text-3xl text-[color:var(--s-ink)] sm:text-4xl">
            {mode === "link" ? "Get your private link" : "Welcome back"}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--s-mute)]">
            {mode === "link"
              ? "Enter the email address we have on file and we'll send a link straight to your projects."
              : "Use the password you set on your last visit."}
          </p>

          {sent ? (
            <div
              className="mt-7 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
              data-testid="portal-link-sent"
            >
              If that email is on one of our projects, your link is on its way.
              It works for 30 days.
            </div>
          ) : (
            <div className="mt-7 space-y-4">
              <label className="block">
                <span className="s-micro text-[color:var(--s-mute)]">Email</span>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-[color:var(--s-line)] bg-white px-3.5 transition-colors duration-200 focus-within:border-[color:var(--s-gold)]">
                  <EnvelopeSimple size={16} className="text-[color:var(--s-mute)]" />
                  <input
                    data-testid="portal-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && mode === "link" && requestLink()
                    }
                    placeholder="you@email.com"
                    className="h-12 w-full bg-transparent text-sm text-[color:var(--s-ink)] outline-none placeholder:text-[color:var(--s-mute)]/60"
                  />
                </div>
              </label>

              {mode === "password" && (
                <label className="block">
                  <span className="s-micro text-[color:var(--s-mute)]">
                    Password
                  </span>
                  <div className="mt-2 flex items-center gap-2 rounded-xl border border-[color:var(--s-line)] bg-white px-3.5 transition-colors duration-200 focus-within:border-[color:var(--s-gold)]">
                    <LockKey size={16} className="text-[color:var(--s-mute)]" />
                    <input
                      data-testid="portal-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && signIn()}
                      placeholder="Your password"
                      className="h-12 w-full bg-transparent text-sm text-[color:var(--s-ink)] outline-none placeholder:text-[color:var(--s-mute)]/60"
                    />
                  </div>
                </label>
              )}

              <button
                type="button"
                data-testid="portal-submit"
                disabled={busy || !email.trim()}
                onClick={mode === "link" ? requestLink : signIn}
                className="s-micro inline-flex h-12 w-full items-center justify-center rounded-full bg-[color:var(--s-gold)] !text-[12px] text-white transition-colors duration-300 hover:bg-[#a9855a] disabled:opacity-40"
              >
                {busy
                  ? "Working…"
                  : mode === "link"
                    ? "Email me my link"
                    : "Sign in"}
              </button>
            </div>
          )}

          <button
            type="button"
            data-testid="portal-switch-mode"
            onClick={() => {
              setMode(mode === "link" ? "password" : "link");
              setSent(false);
            }}
            className="s-micro mt-6 text-[color:var(--s-gold)] underline-offset-4 transition-colors duration-200 hover:underline"
          >
            {mode === "link" ? "I have a password" : "Email me a link instead"}
          </button>
        </div>
      </div>
    </div>
  );
}
